/**
 * useExtractPalette — Extracts a 5-color palette from a listing image.
 *
 * Uses k-means clustering on a 16x16 canvas (256 pixels) to find the
 * 5 most representative RGB colors. Returns the palette and whether
 * the image is muted (light/desaturated).
 *
 * Used by StarBackground to derive star colors from listing images.
 */
import { useEffect, useState, useRef } from 'react';

const CANVAS_SIZE = 16;
const K = 5;
const MAX_ITERATIONS = 10;
const LUMINANCE_THRESHOLD = 0.6;
const SATURATION_THRESHOLD = 0.15;

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }

  return { s, l };
}

function kMeans(pixels) {
  // Initialize centroids by picking k evenly spaced pixels
  const step = Math.max(1, Math.floor(pixels.length / K));
  let centroids = [];
  for (let i = 0; i < K; i++) {
    const idx = Math.min(i * step, pixels.length - 1);
    centroids.push([...pixels[idx]]);
  }

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    // Assign pixels to nearest centroid
    const clusters = Array.from({ length: K }, () => []);

    for (const px of pixels) {
      let minDist = Infinity;
      let closest = 0;
      for (let c = 0; c < K; c++) {
        const dr = px[0] - centroids[c][0];
        const dg = px[1] - centroids[c][1];
        const db = px[2] - centroids[c][2];
        const dist = dr * dr + dg * dg + db * db;
        if (dist < minDist) {
          minDist = dist;
          closest = c;
        }
      }
      clusters[closest].push(px);
    }

    // Recompute centroids
    let converged = true;
    for (let c = 0; c < K; c++) {
      if (clusters[c].length === 0) continue;
      const newR = Math.round(clusters[c].reduce((s, p) => s + p[0], 0) / clusters[c].length);
      const newG = Math.round(clusters[c].reduce((s, p) => s + p[1], 0) / clusters[c].length);
      const newB = Math.round(clusters[c].reduce((s, p) => s + p[2], 0) / clusters[c].length);

      if (newR !== centroids[c][0] || newG !== centroids[c][1] || newB !== centroids[c][2]) {
        converged = false;
      }
      centroids[c] = [newR, newG, newB];
    }

    if (converged) break;
  }

  // Sort by cluster saturation (most saturated first — more interesting colors)
  centroids.sort((a, b) => {
    const sa = rgbToHsl(a[0], a[1], a[2]).s;
    const sb = rgbToHsl(b[0], b[1], b[2]).s;
    return sb - sa;
  });

  return centroids;
}

export default function useExtractPalette(imageUrl) {
  const [result, setResult] = useState(null);
  const prevUrlRef = useRef(null);

  useEffect(() => {
    if (!imageUrl || typeof window === 'undefined') return;
    if (imageUrl === prevUrlRef.current) return;
    prevUrlRef.current = imageUrl;

    const run = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_SIZE;
        canvas.height = CANVAS_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

        try {
          const imageData = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          const { data } = imageData;

          // Extract pixel array and compute overall stats
          const pixels = [];
          let totalL = 0;
          let totalS = 0;
          const pixelCount = data.length / 4;

          for (let i = 0; i < data.length; i += 4) {
            pixels.push([data[i], data[i + 1], data[i + 2]]);
            const { s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2]);
            totalL += l;
            totalS += s;
          }

          const avgLuminance = totalL / pixelCount;
          const avgSaturation = totalS / pixelCount;
          const isMuted = avgLuminance > LUMINANCE_THRESHOLD || avgSaturation < SATURATION_THRESHOLD;

          const palette = kMeans(pixels);

          setResult({ palette, isMuted });
        } catch {
          // CORS or tainted canvas
          setResult(null);
        }
      };

      img.onerror = () => setResult(null);
      img.src = imageUrl;
    };

    // Schedule during idle time
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(run);
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(run, 50);
      return () => clearTimeout(id);
    }
  }, [imageUrl]);

  return result;
}
